import type { Priority } from './types'

/**
 * Board filters live in the URL, not in component state.
 *
 * That single decision buys three things: a filtered board is a link you can
 * paste to a colleague, the back button behaves, and the AI export can send
 * the very same query string to the server so the export contains exactly what
 * is on screen.
 */
export interface Filters {
  assignees: string[]
  unassigned: boolean
  tags: string[]
  tagsMatchAll: boolean
  priorities: Priority[]
  due: '' | 'overdue' | 'today' | 'week' | 'none' | 'attention'
  q: string
  archived: boolean
}

export const EMPTY_FILTERS: Filters = {
  assignees: [],
  unassigned: false,
  tags: [],
  tagsMatchAll: false,
  priorities: [],
  due: '',
  q: '',
  archived: false,
}

export function parseFilters(params: URLSearchParams): Filters {
  const assignees: string[] = []
  let unassigned = false
  for (const raw of params.getAll('assignee')) {
    for (const part of raw.split(',')) {
      const value = part.trim()
      if (!value) continue
      if (value === 'unassigned' || value === 'none') unassigned = true
      else assignees.push(value)
    }
  }

  const priorities = params
    .getAll('priority')
    .flatMap((raw) => raw.split(','))
    .map((p) => p.trim())
    .filter((p): p is Priority => ['low', 'medium', 'high', 'urgent'].includes(p))

  const due = params.get('due') ?? ''

  return {
    assignees,
    unassigned,
    tags: params.getAll('tag').flatMap((raw) => raw.split(',')).filter(Boolean),
    tagsMatchAll: params.get('tagMode') === 'all',
    priorities,
    due: (['overdue', 'today', 'week', 'none', 'attention'].includes(due)
      ? due
      : '') as Filters['due'],
    q: params.get('q') ?? '',
    archived: params.get('archived') === 'true',
  }
}

/** Serialises filters back into the exact parameter names the API expects. */
export function filtersToParams(f: Filters): URLSearchParams {
  const params = new URLSearchParams()
  for (const id of f.assignees) params.append('assignee', id)
  if (f.unassigned) params.append('assignee', 'unassigned')
  for (const id of f.tags) params.append('tag', id)
  if (f.tagsMatchAll && f.tags.length > 1) params.set('tagMode', 'all')
  for (const p of f.priorities) params.append('priority', p)
  if (f.due) params.set('due', f.due)
  if (f.q.trim()) params.set('q', f.q.trim())
  if (f.archived) params.set('archived', 'true')
  return params
}

export function filterQuery(f: Filters): string {
  const params = filtersToParams(f)
  const s = params.toString()
  return s ? `?${s}` : ''
}

export function activeFilterCount(f: Filters): number {
  return (
    f.assignees.length +
    (f.unassigned ? 1 : 0) +
    f.tags.length +
    f.priorities.length +
    (f.due ? 1 : 0) +
    (f.q.trim() ? 1 : 0) +
    (f.archived ? 1 : 0)
  )
}

export function isEmpty(f: Filters): boolean {
  return activeFilterCount(f) === 0
}

export const DUE_LABELS: Record<Exclude<Filters['due'], ''>, string> = {
  overdue: 'Overdue',
  today: 'Due today',
  week: 'Due this week',
  attention: 'Needs attention',
  none: 'No due date',
}
